import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Crown, TrendingUp, Briefcase, Users, DollarSign, Percent,
  CheckCircle2, AlertCircle, Award, Clock, Receipt, Activity
} from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, icon: Icon, accent, sub, testid }) {
  return (
    <Card className="rounded-3xl p-6 border-border/60 glass hover:-translate-y-1 transition-all duration-300" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${accent}`}><Icon className="w-5 h-5" /></div>
        <TrendingUp className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="text-3xl font-heading font-bold">{value}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

const STATUS_LABEL = { scheduled: "Geplant", in_progress: "Läuft", done: "Fertig", cancelled: "Abgesagt" };

export default function Agency() {
  const [data, setData] = useState(null);
  const [rate, setRate] = useState(20);
  const [saving, setSaving] = useState(false);

  async function load() {
    const [a, c] = await Promise.all([
      api.get("/agency/overview"),
      api.get("/settings/commission"),
    ]);
    setData(a.data);
    setRate(c.data.default_rate);
  }
  useEffect(() => { load(); }, []);

  async function saveRate() {
    setSaving(true);
    try {
      await api.put("/settings/commission", { default_rate: parseFloat(rate) });
      toast.success(`Standard-Provision auf ${rate}% gesetzt`);
    } catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  }

  if (!data) return <div className="text-muted-foreground">Lade Agentur-Daten...</div>;

  const t = data.totals;
  const inv = data.invoices;
  const profit = (t.commission || 0);
  const profitMargin = t.revenue > 0 ? Math.round((profit / t.revenue) * 100) : 0;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="agency-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-accent">
            <Crown className="w-4 h-4" /> Agentur Master-Dashboard
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Kontrolle & Erträge</h1>
          <p className="text-muted-foreground mt-1">Vollständiger Überblick über alle Umsätze, Provisionen und Aktivitäten.</p>
        </div>
        <Card className="rounded-3xl p-4 border-border/60 glass flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/15 text-accent flex items-center justify-center"><Percent className="w-5 h-5" /></div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Standard-Provision</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="number" value={rate} onChange={e=>setRate(e.target.value)} className="rounded-full h-9 w-20 text-center" data-testid="commission-rate" />
              <span className="text-sm">%</span>
              <Button size="sm" onClick={saveRate} disabled={saving} className="rounded-full h-9" data-testid="save-rate">Speichern</Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Money KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat label="Gesamtumsatz" value={`€ ${t.revenue.toLocaleString('de-DE')}`} icon={DollarSign} accent="bg-primary/15 text-primary"
              sub={`Davon abgeschlossen: € ${t.revenue_done.toLocaleString('de-DE')}`} testid="stat-revenue" />
        <Stat label="Agentur-Provision" value={`€ ${t.commission.toLocaleString('de-DE')}`} icon={Percent} accent="bg-accent/15 text-accent"
              sub={`Marge: ${profitMargin}%`} testid="stat-commission" />
        <Stat label="An Mitarbeiter" value={`€ ${t.payout.toLocaleString('de-DE')}`} icon={Award} accent="bg-violet-500/15 text-violet-400"
              sub="Nettolöhne / Payouts" testid="stat-payout" />
        <Stat label="Provision diesen Monat" value={`€ ${t.commission_month.toLocaleString('de-DE')}`} icon={TrendingUp} accent="bg-pink-500/15 text-pink-400"
              sub={`${t.jobs_month} Einsätze`} testid="stat-month" />
      </div>

      {/* Activity KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat label="Einsätze gesamt" value={t.jobs} icon={Briefcase} accent="bg-primary/15 text-primary" testid="stat-jobs" />
        <Stat label="Abgeschlossen" value={t.jobs_done} icon={CheckCircle2} accent="bg-accent/15 text-accent" testid="stat-done" />
        <Stat label="Team-Mitglieder" value={data.counts.technicians} icon={Users} accent="bg-violet-500/15 text-violet-400" sub={`+${data.counts.customers} Kunden`} testid="stat-team" />
        <Stat label="Rechnungen offen" value={`€ ${inv.open.toLocaleString('de-DE')}`} icon={AlertCircle} accent="bg-amber-500/15 text-amber-500"
              sub={inv.overdue > 0 ? `€ ${inv.overdue} überfällig` : undefined} testid="stat-open" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top performers */}
        <Card className="lg:col-span-2 rounded-3xl p-6 border-border/60 glass" data-testid="top-performers">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-semibold">Leistung pro Mitarbeiter</h3>
            <Badge variant="secondary" className="rounded-full">{data.per_user.length}</Badge>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border/60">
                  <th className="py-2 pr-4 font-semibold">Mitarbeiter</th>
                  <th className="py-2 pr-2 font-semibold text-right">Einsätze</th>
                  <th className="py-2 pr-2 font-semibold text-right">Umsatz</th>
                  <th className="py-2 pr-2 font-semibold text-right">Provision</th>
                  <th className="py-2 font-semibold text-right">Payout</th>
                </tr>
              </thead>
              <tbody>
                {data.per_user.map(p => (
                  <tr key={p.user_id} className="border-b border-border/30 hover:bg-secondary/40 transition" data-testid={`perf-${p.user_id}`}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center font-bold text-sm">{p.name?.[0]?.toUpperCase() || "?"}</div>
                        <div>
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground capitalize">{p.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-2 text-right"><span className="text-muted-foreground">{p.jobs_done}</span>/<span className="font-medium">{p.jobs_total}</span></td>
                    <td className="py-3 pr-2 text-right font-mono">€ {p.revenue.toFixed(2)}</td>
                    <td className="py-3 pr-2 text-right font-mono text-accent">€ {p.commission.toFixed(2)}</td>
                    <td className="py-3 text-right font-mono">€ {p.payout.toFixed(2)}</td>
                  </tr>
                ))}
                {data.per_user.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted-foreground py-8">Noch keine Einsätze zugewiesen.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Recent activity */}
        <Card className="rounded-3xl p-6 border-border/60 glass" data-testid="recent-activity">
          <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2"><Activity className="w-5 h-5 text-primary" /> Live-Aktivität</h3>
          <div className="space-y-3 max-h-[500px] overflow-auto">
            {data.recent.map(r => (
              <div key={r.id} className="flex items-start gap-3 p-3 rounded-2xl hover:bg-secondary/40 transition">
                <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                  r.status === "done" ? "bg-accent" : r.status === "in_progress" ? "bg-amber-500" : "bg-primary"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {r.assigned ? r.assigned : "Unzugewiesen"} · {STATUS_LABEL[r.status] || r.status}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {r.price > 0 && <span className="text-[11px] font-semibold">€ {r.price.toFixed(0)}</span>}
                    {r.commission > 0 && <span className="text-[11px] text-accent">+€ {r.commission.toFixed(2)} Provision</span>}
                  </div>
                </div>
              </div>
            ))}
            {data.recent.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Noch keine Aktivität.</div>}
          </div>
        </Card>
      </div>

      {/* Invoices breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Card className="rounded-3xl p-6 border-border/60 glass" data-testid="inv-paid">
          <div className="flex items-center gap-3 mb-2"><CheckCircle2 className="w-5 h-5 text-accent" /><span className="text-sm font-medium">Bezahlt</span></div>
          <div className="font-heading text-3xl font-bold">€ {inv.paid.toLocaleString('de-DE')}</div>
        </Card>
        <Card className="rounded-3xl p-6 border-border/60 glass" data-testid="inv-open">
          <div className="flex items-center gap-3 mb-2"><Receipt className="w-5 h-5 text-amber-500" /><span className="text-sm font-medium">Offen</span></div>
          <div className="font-heading text-3xl font-bold">€ {inv.open.toLocaleString('de-DE')}</div>
        </Card>
        <Card className="rounded-3xl p-6 border-border/60 glass" data-testid="inv-overdue">
          <div className="flex items-center gap-3 mb-2"><Clock className="w-5 h-5 text-destructive" /><span className="text-sm font-medium">Überfällig</span></div>
          <div className="font-heading text-3xl font-bold">€ {inv.overdue.toLocaleString('de-DE')}</div>
        </Card>
      </div>
    </div>
  );
}
